import {
  Badge,
  Button,
  Drawer,
  Flex,
  Grid,
  Popover,
  Typography,
  theme
} from 'antd'
import type { ButtonProps, DrawerProps, PopoverProps } from 'antd'
import { Funnel, RotateCw } from 'lucide-react'
import { useState } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useIconSizes } from 'src/hooks'

const { Text, Title } = Typography
const { useBreakpoint } = Grid

export type MoreFiltersProps = {
  title?: string
  drawerTitle?: ReactNode
  filterActiveCount?: number
  onResetMoreFilters?: () => void
  onOpenChange?: (open: boolean) => void
  buttonProps?: ButtonProps
} & Omit<DrawerProps, 'title'>

export type MoreFiltersColProps = PropsWithChildren<{
  title: ReactNode
}>

const MoreFiltersCol = ({ title, children }: MoreFiltersColProps) => (
  <Flex vertical gap={4}>
    <Text>{title}</Text>
    {children}
  </Flex>
)

const MoreFiltersBase = ({
  title,
  drawerTitle,
  filterActiveCount,
  children,
  onResetMoreFilters,
  onOpenChange,
  buttonProps,
  open,
  onClose,
  size,
  extra,
  footer = false,
  ...props
}: MoreFiltersProps) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const screens = useBreakpoint()
  const [innerOpen, setInnerOpen] = useState(false)

  const isOpen = open ?? innerOpen
  const resolvedWidth = size ?? (screens.md ? 420 : '100%')

  const setDrawerOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInnerOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
  }

  const handleClose: DrawerProps['onClose'] = (event) => {
    closeDrawer()
    onClose?.(event)
  }

  const handleTriggerClick: ButtonProps['onClick'] = (event) => {
    buttonProps?.onClick?.(event)

    if (!event?.defaultPrevented) {
      setDrawerOpen(true)
    }
  }

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={handleClose}
        placement="right"
        size={resolvedWidth}
        title={
          <Flex align="center" gap={8}>
            <Title level={5} style={{ marginBottom: 0 }}>
              {drawerTitle ?? title ?? t('more_filters')}
            </Title>

            {(filterActiveCount ?? 0) > 0 && (
              <Badge color={token.colorPrimary} count={filterActiveCount} />
            )}
          </Flex>
        }
        extra={
          <Flex align="center" gap={8}>
            {onResetMoreFilters && (
              <Button
                type="text"
                icon={<RotateCw size={14} />}
                onClick={onResetMoreFilters}
                disabled={(filterActiveCount ?? 0) === 0}
              >
                {t('reset')}
              </Button>
            )}

            {extra}
          </Flex>
        }
        footer={
          footer ?? (
            <Flex justify="space-between" align="center">
              <Button onClick={closeDrawer}>{t('cancel')}</Button>

              <Button type="primary" onClick={closeDrawer}>
                {t('apply')}
              </Button>
            </Flex>
          )
        }
        {...props}
      >
        <Flex vertical gap="middle">
          {children}
        </Flex>
      </Drawer>

      <Badge color={token.colorPrimary} count={filterActiveCount}>
        <Button icon={<Funnel />} {...buttonProps} onClick={handleTriggerClick}>
          {title ?? t('more_filters')}
        </Button>
      </Badge>
    </>
  )
}

export const MoreFilters = Object.assign(MoreFiltersBase, {
  Col: MoreFiltersCol
})

export type MoreFiltersPopoverProps = {
  title?: string
  filterActiveCount?: number
  onResetMoreFilters?: () => void
  buttonProps?: ButtonProps
} & PopoverProps

export const MoreFiltersPopover = ({
  title,
  filterActiveCount,
  children,
  onResetMoreFilters,
  buttonProps,
  ...props
}: MoreFiltersPopoverProps) => {
  const { t } = useTranslation()
  const iconSizes = useIconSizes()
  const { token } = theme.useToken()

  return (
    <Popover
      content={
        <Flex vertical gap={16}>
          {children}
        </Flex>
      }
      title={
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={4}>
            <Title level={5} style={{ marginBottom: 0 }}>
              {t('filters')}
            </Title>

            {(filterActiveCount ?? 0) > 0 && (
              <Badge color={token.colorPrimary} count={filterActiveCount} />
            )}
          </Flex>

          {onResetMoreFilters && (
            <Button
              type="text"
              icon={<RotateCw size={iconSizes.small} />}
              size="small"
              onClick={onResetMoreFilters}
              disabled={(filterActiveCount ?? 0) === 0}
            >
              {t('reset')}
            </Button>
          )}
        </Flex>
      }
      trigger="click"
      {...props}
    >
      <Badge color={token.colorPrimary} count={filterActiveCount}>
        <Button
          icon={<Funnel size={iconSizes.small} />}
          size="small"
          {...buttonProps}
        >
          {title ?? t('more_filters')}
        </Button>
      </Badge>
    </Popover>
  )
}
